import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormularioDialogService } from '../../../../../auxiliares/servicios/formulario-dialog.service';
import { ActivatedRoute, Router } from '@angular/router';
import { OperadorSchema } from 'modelos';
import { IQueryParam } from 'modelos/src';
import { Subscription } from 'rxjs';
import { AuxiliaresModule } from '../../../../../auxiliares/auxiliares.module';
import { DialogService } from '../../../../../auxiliares/dialog/dialog.service';
import { ClienteJerarquiaService } from '../../../../../auxiliares/servicios/cliente-jerarquia.service';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import {
  EstadoSimFiltro,
  ISimConEstado,
  SimsService,
} from '../../../../../auxiliares/servicios/http/sims.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { rutaDetalleEntidad } from '../../../../../auxiliares/servicios/rutas-entidades';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../../../auxiliares/tabla/filtro/filtro.component';
import {
  IColumnas,
  ICrearBoton,
  IRowLink,
} from '../../../../../auxiliares/tabla/tabla.component';
import {
  IEstadoCelda,
  TonoEstado,
} from '../../../../../auxiliares/tabla/tabla.interfaces';
import { escaparHtml } from '../../../../../auxiliares/html-seguro.util';
import { CrearEditarSimComponent } from '../crear-editar-sim/crear-editar-sim.component';

/**
 * Estado de cada SIM en la tabla, con el texto del chip y su tooltip. Lo
 * comparten el listado y el detalle.
 */
export function chipEstadoSim(sim: ISimConEstado): IRowLink {
  const estado = SimsService.estado(sim);
  const clase = {
    Disponible: 'disponible',
    Asignada: 'asignada',
    'En conflicto': 'conflicto',
  }[estado];
  const tooltip = {
    Disponible: 'No está puesta en ningún equipo',
    Asignada: 'Puesta en un equipo',
    'En conflicto':
      'Figura puesta en más de un equipo a la vez: hay que corregir alguno',
  }[estado];
  return { link: estado, class: `estado-sim ${clase}`, tooltip };
}

/** Estado de la SIM como pieza `estado` del listado (mismo texto y tooltip
 *  que el chip del detalle). */
export function estadoSimCelda(sim: ISimConEstado): IEstadoCelda {
  const { link, tooltip } = chipEstadoSim(sim);
  const estado = SimsService.estado(sim);
  const [tono, icono]: [TonoEstado, string] =
    estado === 'Disponible'
      ? ['ok', 'check_circle']
      : estado === 'Asignada'
        ? ['info', 'sim_card']
        : ['error', 'error'];
  return { texto: link, tono, icono, tooltip };
}

/** Links a los equipos que hoy tienen puesta la SIM. */
export function linksEquiposSim(sim: ISimConEstado): IRowLink[] {
  return (sim.asignacionesVigentes ?? []).map((a) => {
    const nombre = `${a.nombreEntidad || 'Sin nombre'} (${a.tipoEntidad})`;
    const ruta = rutaDetalleEntidad(a.tipoEntidad, a.idEntidad);
    return ruta ? { link: nombre, route: ruta } : { link: nombre };
  });
}

@Component({
  selector: 'app-listado-sims',
  standalone: true,
  imports: [AuxiliaresModule],
  templateUrl: './listado-sims.component.html',
})
export class ListadoSimsComponent implements OnInit, OnDestroy {
  helper = inject(HelperService);
  private service = inject(SimsService);
  private lista2 = inject(Lista2Service);
  private clienteJerarquia = inject(ClienteJerarquiaService);
  private dialogService = inject(DialogService);
  private formularios = inject(FormularioDialogService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  public name = 'ListadoSimsComponent';
  public datos?: ISimConEstado[];
  public totalCount = 0;
  public columnas?: IColumnas<ISimConEstado>[];
  private sims$?: Subscription;

  public botonCrear: ICrearBoton = {
    mostrar: this.service.puedeCrear(),
    tooltip: 'Crear SIM',
    color: 'primary',
    tipo: 'material',
    icon: 'add',
    accion: () => this.crear(),
  };

  public search: IRegExpSearch = {
    fields: ['iccid', 'numero'],
  };

  // FILTROS
  // `estado` no es un campo de la SIM: el filtro de la tabla lo deja dentro
  // de query.filter y listar() lo pasa como query param aparte.
  public estado: IFiltroTabla = {
    elementos: [
      { label: 'Disponible', value: 'disponible' },
      { label: 'Asignada', value: 'asignada' },
      { label: 'En conflicto', value: 'conflicto' },
    ],
    filter: { field: 'estado' },
    label: 'Estado',
    rapido: true,
    tipo: 'ngselect',
    selectLabel: 'label',
    selectValue: 'value',
  };
  public operador: IFiltroTabla = {
    elementos: OperadorSchema.options,
    filter: { field: 'operador' },
    label: 'Operador',
    tipo: 'ngselect',
  };
  public cliente: IFiltroTabla = {
    filter: { field: 'idCliente' },
    label: 'Cliente',
    tipo: 'cliente-selector',
  };
  // Cliente en cuya jerarquía cuelga la SIM: el cliente elegido y toda su
  // descendencia.
  public ancestro: IFiltroTabla = {
    filter: { field: 'idsAncestros', incluirPropio: true },
    label: 'En la jerarquía de',
    tipo: 'cliente-selector',
  };
  public filtros: IFiltroTabla[] = [this.estado, this.operador];

  public query: IQueryParam = {
    sort: 'iccid',
    page: 0,
    limit: +this.helper.getPageSize(this.name),
    includeChildren: true,
  };

  /// Listado

  /**
   * Saca `estado` del filtro armado por la tabla (va en `$and` o al tope)
   * y devuelve el filtro sin él, para mandarlo como query param.
   */
  private separarEstado(filterJson?: string): {
    filter?: string;
    estado?: EstadoSimFiltro;
  } {
    if (!filterJson) return {};
    const filter: Record<string, any> = JSON.parse(filterJson);
    let estado: EstadoSimFiltro | undefined;
    if (filter['estado']) {
      estado = filter['estado'];
      delete filter['estado'];
    }
    if (Array.isArray(filter['$and'])) {
      filter['$and'] = filter['$and'].filter((f: any) => {
        if (f?.estado === undefined) return true;
        estado = f.estado;
        return false;
      });
      if (!filter['$and'].length) delete filter['$and'];
    }
    return { filter: JSON.stringify(filter), estado };
  }

  public listar(): void {
    const { filter, estado } = this.separarEstado(this.query.filter);
    const query: IQueryParam = { ...this.query, filter };
    this.sims$?.unsubscribe();
    this.sims$ = this.lista2.sims(query, 30, estado).subscribe((data) => {
      this.totalCount = data.totalCount;
      this.datos = data.datos;
    });
  }

  /// Acciones

  public detalles(sim: ISimConEstado): void {
    this.router.navigate([sim._id], { relativeTo: this.route });
  }

  public crear(): void {
    this.formularios
      .abrir(CrearEditarSimComponent, { tamano: 'mediano', data: {} })
      .afterClosed()
      .subscribe((res) => {
        if (res) this.listar();
      });
  }

  public editar(sim: ISimConEstado): void {
    this.formularios
      .abrir(CrearEditarSimComponent, {
        tamano: 'mediano',
        data: { edit: sim },
      })
      .afterClosed()
      .subscribe((res) => {
        if (res) this.listar();
      });
  }

  public async eliminar(sim: ISimConEstado): Promise<void> {
    const ok = await this.dialogService.confirm(
      'Eliminar SIM',
      `¿Querés eliminar la SIM ${SimsService.nombre(sim)}? Su historial de asignaciones se conserva.`,
    );
    if (!ok) return;
    try {
      await this.service.delete(sim._id!);
      this.helper.notifSuccess('SIM eliminada');
      this.listar();
    } catch (error) {
      // 409: la SIM quedó asignada a un equipo desde que se cargó la fila
      this.helper.notifError(error);
    }
  }

  /// Tabla

  private setColumnas(): void {
    const columnas: IColumnas<ISimConEstado>[] = [
      {
        header: { label: 'ICCID', sortable: true, sort: 'iccid' },
        row: {
          html: (dato) =>
            dato.iccid
              ? `<span class="tabla-codigo">${escaparHtml(dato.iccid)}</span>`
              : '',
          noData: 'Sin ICCID',
        },
      },
      {
        header: { label: 'Número', sortable: true },
        row: { field: 'numero', noData: 'Sin número' },
      },
      {
        header: { label: 'Operador', sortable: true },
        row: { field: 'operador', noData: 'Sin operador' },
      },
      {
        header: { label: 'APN', sortable: true },
        row: { field: 'apn', noData: 'Sin APN' },
      },
      {
        header: { label: 'Estado' },
        row: {
          estado: (dato) => estadoSimCelda(dato),
        },
      },
      // Equipos que hoy la tienen puesta (antes debajo del chip de estado)
      {
        header: { label: 'Equipo' },
        row: {
          links: (dato) => linksEquiposSim(dato),
          noData: 'Sin equipo',
        },
      },
    ];

    if (HelperService.getTipoCliente() !== 'Final') {
      columnas.push({
        header: { label: 'Cliente' },
        row: {
          // Las SIMs no traen el cliente populado: el servicio de jerarquía
          // lo resuelve por id contra los clientes visibles.
          html: (dato) => this.clienteJerarquia.htmlNombre(dato.idCliente),
          tooltip: (dato) =>
            this.clienteJerarquia.tooltipJerarquia(dato.idCliente),
          noData: 'Sin cliente',
        },
      });
    }

    columnas.push({
      header: { label: 'Acciones' },
      row: {
        acciones: [
          {
            tipo: 'material',
            icon: 'visibility',
            tooltip: 'Ver detalle',
            click: (dato) => this.detalles(dato),
          },
          {
            tipo: 'material',
            icon: 'edit',
            tooltip: 'Editar',
            click: (dato) => this.editar(dato),
            oculta: () => !this.service.puedeEditar(),
          },
          {
            tipo: 'material',
            color: 'warn',
            icon: 'delete',
            tooltip: 'Eliminar',
            click: (dato) => this.eliminar(dato),
            oculta: () => !this.service.puedeEliminar(),
            // Con una asignación vigente el backend responde 409: primero
            // hay que sacarla del equipo.
            disabled: (dato) => SimsService.estado(dato) !== 'Disponible',
          },
        ],
      },
    });

    this.columnas = columnas;
  }

  /// Hooks

  ngOnInit(): void {
    if (HelperService.getTipoCliente() !== 'Final') {
      this.filtros.push(this.cliente, this.ancestro);
    }
    this.setColumnas();
    this.listar();
  }

  ngOnDestroy(): void {
    this.sims$?.unsubscribe();
  }
}
